"use client";

import { useState } from "react";
import {
  X,
  ChevronLeft,
  Flag,
  EyeOff,
  UserX,
  AlertTriangle,
  MessageSquareWarning,
  Shield,
  Baby
} from "lucide-react";
import { errorToast, successToast } from "../../utils/toasters";

const reportReasons = [
  {
    id: "spam",
    title: "Spam",
    description: "Repetitive or unwanted content",
    icon: AlertTriangle
  },
  {
    id: "harassment",
    title: "Harassment & Threats",
    description: "Bullying, threatening, or harassing behavior",
    icon: MessageSquareWarning
  },
  {
    id: "profanity",
    title: "Profanity & Vulgar",
    description: "Offensive or inappropriate language",
    icon: Shield
  },
  {
    id: "child-safety",
    title: "Child Safety",
    description: "Content that may harm children",
    icon: Baby
  }
];

const reportActions = [
  {
    id: "hide_post",
    title: "Hide this post",
    description: "You won't see this post in your feed",
    icon: EyeOff
  },
  {
    id: "block_user",
    title: "Block user",
    description: "You won't see posts from this user anymore",
    icon: UserX
  }
];

export default function ReportModal({ isOpen, onClose, onReport, username }) {
  const [step, setStep] = useState("reason");
  const [selectedReason, setSelectedReason] = useState("");
  const [selectedAction, setSelectedAction] = useState("");

  const handleReasonNext = () => {
    if (selectedReason) {
      setStep("action");
    }
  };

  const handleBack = () => {
    setStep("reason");
    setSelectedAction("");
  };

  const handleSubmit = async () => {
    try {
      if (selectedReason && selectedAction) {
        await onReport(selectedReason, selectedAction);
        handleClose();
        successToast(
          `Post reported for ${selectedReason}. ${
            selectedAction === "hide_post"
              ? "Post hidden from feed."
              : "User blocked."
          }`
        );
      } else {
        throw new Error("Reason and action are required");
      }
    } catch (error) {
      console.error("Error while reporting user: ", error);
      errorToast("Error while reporting user");
    }
  };

  const handleClose = () => {
    setStep("reason");
    setSelectedReason("");
    setSelectedAction("");
    onClose();
  };

  const selectedReasonData = reportReasons.find(r => r.id === selectedReason);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            {step === "action" && (
              <button
                onClick={handleBack}
                className="p-1 h-8 w-8 rounded-md hover:bg-gray-100 transition-colors flex items-center justify-center"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            <h2 className="text-xl font-semibold">
              {step === "reason" ? "Report post" : "What would you like to do?"}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1 h-8 w-8 rounded-md hover:bg-gray-100 transition-colors flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === "reason" && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Help us understand what's happening with this post.
              </p>

              <div className="space-y-3">
                {reportReasons.map(reason => {
                  const IconComponent = reason.icon;
                  return (
                    <label
                      key={reason.id}
                      className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <input
                        type="radio"
                        name="reportReason"
                        value={reason.id}
                        checked={selectedReason === reason.id}
                        onChange={e => setSelectedReason(e.target.value)}
                        className="mt-1 h-4 w-4 text-red-600 border-gray-300 focus:ring-red-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <IconComponent className="h-4 w-4 text-gray-600" />
                          <span className="font-medium text-gray-900">
                            {reason.title}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          {reason.description}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={handleReasonNext}
                  disabled={!selectedReason}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {step === "action" && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center space-x-2 text-sm">
                  <Flag className="h-4 w-4 text-red-500" />
                  <span className="font-medium">
                    Reporting for: {selectedReasonData?.title}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                {reportActions.map(action => {
                  const IconComponent = action.icon;
                  return (
                    <label
                      key={action.id}
                      className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <input
                        type="radio"
                        name="reportAction"
                        value={action.id}
                        checked={selectedAction === action.id}
                        onChange={e => setSelectedAction(e.target.value)}
                        className="mt-1 h-4 w-4 text-red-600 border-gray-300 focus:ring-red-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <IconComponent className="h-4 w-4 text-gray-700" />
                          <span className="font-medium text-gray-900">
                            {action.title}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          {action.description}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <button
                  onClick={handleBack}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!selectedAction}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Submit Report
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
